import React, { useRef, useState } from 'react'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import IconSignaturePen from '@assets/images/icons/signature_pen.svg'
import IconMoreMenu from '@assets/images/icons/more_menu.svg'
import IconCheckThin from '@assets/images/icons/check_thin.svg'
import IconTrashCan from '@assets/images/icons/trashcan.svg'
import IconPreview from '@assets/images/icons/search.svg'
import { SquareIconButton } from '@/modules/base/components/buttons'
import PopperMenu from '@/modules/base/components/popper-menu/popper-menu'
import ThemeInterface from '@/theme-interface'
import { useSignatureContext } from '@/modules/signature/_helpers/SignatureContext'
import { SignatureItem } from '@/modules/signature/types'
import { SIGNATURE_FIELD_TYPES } from '@/modules/base/util/constants'
import { getFieldTypeInfo } from '@/modules/signature/_helpers/getFieldTypeInfo'

const moreButtonLang = lang.signature.signing.moreButton

const useStyles = makeStyles((theme: ThemeInterface) => ({
  popperContent: {
    padding: '0px 17px !important',
  },
  item: {
    display: 'flex',
    padding: '17px 0px 14px 0px',
    borderBottom: `1px solid ${theme.borderColors.default}`,
    cursor: 'pointer',
    '&:last-of-type': {
      borderBottom: 'none',
    },
    '&:hover': {
      color: theme.primary,
    },
  },
  label: {
    marginLeft: 18,
  },
}), { name: 'SignatureSigningItemMoreButton' })

type SignatureSigningMoreButtonProps = {
  type: string,
  index: number,
  disabled: boolean,
  signatureItem: SignatureItem
}

const SignatureSigningMoreButton = (props: SignatureSigningMoreButtonProps) => {
  const classes = useStyles()
  const { type, index, disabled, signatureItem } = props
  const {
    position,
    signature: signing,
  } = signatureItem
  const [popperOpen, setPopperOpen] = useState(false)
  const popperRef = useRef()

  const {
    setCurrentItemIndex,
    resetItem,
    addSigningToAll,
    openSigningModal,
    pager: { setCurrentPage },
  } = useSignatureContext()

  const onEdit = (): void => {
    openSigningModal(index, false)
  }

  const onApplyToAll = (): void => {
    if (!disabled) { return }
    addSigningToAll(signing)
  }

  const onDelete = (): void => {
    resetItem(index)
  }

  const onShow = (): void => {
    setCurrentPage(position.page)
    setCurrentItemIndex(index)
  }

  let items = [
    { icon: IconPreview, label: moreButtonLang.items.show, onClick: onShow },
  ]
  if (![SIGNATURE_FIELD_TYPES.DATE, SIGNATURE_FIELD_TYPES.TEXT].includes(type)) {
    items = [
      ...items,
      { icon: IconSignaturePen, label: moreButtonLang.items.edit, onClick: onEdit },
      { icon: IconCheckThin, label: moreButtonLang.items.applyToAll, onClick: onApplyToAll },
    ]
  }
  items.push({ icon: IconTrashCan, label: moreButtonLang.items.delete(getFieldTypeInfo(signatureItem).label), onClick: onDelete })

  const itemEls = items.map((item) => {
    const { icon, label, onClick } = item
    const handleClick = (): void => {
      setPopperOpen(false)
      onClick?.()
    }
    return (
      <div className={classes.item} onClick={handleClick}>
        <SVGIcon svg={icon} height="16px" width="16px" />
        <span className={classes.label}>{label}</span>
      </div>
    )
  })

  return (
    <>
      <div ref={popperRef}>
        <SquareIconButton
          size="large"
          icon={IconMoreMenu}
          disabled={disabled}
          onClick={() => setPopperOpen(true)} />
      </div>
      {popperOpen && (
        <PopperMenu
          open={true}
          elementRef={popperRef.current}
          style={{ popperContent: classes.popperContent }}
          onClose={() => setPopperOpen(false)}>
          {itemEls}
        </PopperMenu>
      )}
    </>
  )
}

export default SignatureSigningMoreButton
